/**
 * ProductGrid — the server-filtered product listing.
 *
 * All filtering/searching/sorting is delegated to the server via {@link useProducts};
 * this widget only renders the result and routes taps to ``onSelect``.
 */
import {
  ProductCard,
  useProducts,
  type ProductListItem,
  type ProductListParams,
} from "@entities/product";

import { StatePanel } from "./StatePanel";
import styles from "./CatalogBrowser.module.css";

/** Optional cart wiring (ТЗ §8): enables the "+"/stepper + selection highlight per card. */
export interface ProductGridCart {
  readonly qtyOf: (productId: number) => number;
  readonly onAdd: (product: ProductListItem) => void;
  readonly onIncrement: (productId: number) => void;
  readonly onDecrement: (productId: number) => void;
}

export interface ProductGridProps {
  readonly params: ProductListParams;
  readonly onSelect: (product: ProductListItem) => void;
  /** When set, cards render the local-cart controls instead of a bare "open" plus. */
  readonly cart?: ProductGridCart;
}

export function ProductGrid({
  params,
  onSelect,
  cart,
}: ProductGridProps): JSX.Element {
  const { data, isLoading, error, refetch } = useProducts(params);

  if (isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (error) {
    return <StatePanel kind="error" onRetry={refetch} />;
  }
  if (!data || data.length === 0) {
    return <StatePanel kind="empty" message="Ничего не найдено" />;
  }

  return (
    <div className={styles.productList}>
      {data.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          onSelect={onSelect}
          {...(cart !== undefined
            ? {
                cart: {
                  qty: cart.qtyOf(product.id),
                  onAdd: cart.onAdd,
                  onIncrement: cart.onIncrement,
                  onDecrement: cart.onDecrement,
                },
              }
            : {})}
        />
      ))}
    </div>
  );
}
